<script setup>
import { VNavigationDrawer } from 'vuetify/lib/components/index.mjs'
import BoardCommentList from './BoardCommentList.vue'
import { saveComment, getComments, editComment, delComment } from '@/api/boardCommentApi.js'
import { DEV_MODE } from '@/config/dev-mode'
import CommentActionMenu from './CommentActionMenu.vue'
import ConfirmComponent from '@/components/common/Confirm.vue'
import CommentReportDrawer from './CommentReportDrawer.vue'
import IconLocker from '../svg/IconLocker.vue'
import { useBackLayer } from '@/composables/useAppBack';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  articleId: {
    type: [Number, null],
    default: null,
  },
  initialComments: {
    type: Array,
    default: null,
  },
  articleHasComment: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:modelValue'])

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})
const actionOpen = ref(false)
const targetId = ref(null)
const targetComment = ref(null)
/** 대댓글 시 원댓글 작성자 이름 */
const replyToAuthorName = ref('')
/** 신고 드로어 */
const isReportOpen = ref(false)

const dialogTitle = ref('')
const dialogTitle2 = ref('')
const dialogText = ref('')
const dialogNextUrl = ref('')
const dialogConfirm = ref(null)
const warningTxt = ref('')
const kebabType = ref('')

const currentUserId = computed(() => sessionStorage.getItem('authUserId') ?? null)
const loginUserId = ref(null)

const params = {
  InquiryStatus: '',
  systemId: 5,
  name: '',
  upperCommentId: null,
  upperWriterUserId: null,
  commentType: 'ARTICLE',
  writerUserId: null,
  files: [],
  isPrivate: false,
}
function buildPayload() {
  return {
    ...params,
    name: commentText.value,
    writerUserId: currentUserId.value || loginUserId.value,
  }
}

const hasText = computed(() => commentText.value.trim().length > 0)
const comments = ref(null)
const editingId = ref(null) // 수정 댓글 ID저장용
const isEditing = computed(() => editingId.value != null)
const submitLabel = computed(() => (isEditing.value ? '수정' : '등록'))
const isReplying = ref(false)
const upperComment = ref(null)
const isPrivateComment = ref(false)

// BoardCommentList 컴포넌트 참조
const commentListRef = ref(null)
// 현재 페이지 추적
const currentPage = ref(1)

let inFlight = false
async function fetchComments(articleId, page = 1) {
  if (!articleId || inFlight) return
  inFlight = true
  // loading.value = true
  // error.value = null
  try {
    let targetPage = page
    if (page === 'last') {
      // 댓글 수를 먼저 가져와서 마지막 페이지 계산
      const { getCommentCount } = await import('@/api/boardCommentApi')
      const cnt = await getCommentCount(articleId, 'ARTICLE')
      const totalCount = Number(cnt) || 0
      const PAGE_SIZE = 10
      targetPage = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1
    }
    const res = await getComments(articleId, 'ARTICLE', { Page: targetPage })
    comments.value = res
    // 현재 페이지 업데이트
    currentPage.value = targetPage
  } catch (e) {
    // error.value = e
  } finally {
    // loading.value = false
    inFlight = false
  }
}

/** 페이지 변경 핸들러 */
const handlePageChange = (p) => {
  currentPage.value = p
  fetchComments(props.articleId, p)
}

watch(
  () => [open.value, props.articleId],
  ([isOpen, id]) => {
    if (!isOpen || !id) return
    if (isOpen) {
      loginUserId.value = sessionStorage.getItem('authUserId')
      // 드로어가 열릴 때 body 스크롤 비활성화
      document.body.style.overflow = 'hidden'

      // 스크롤 이벤트 리스너 등록
      nextTick(() => {
        // 여러 스크롤 가능한 요소들에 이벤트 리스너 등록
        const scrollSelectors = [
          '.comment-drawer__list',
          '.board-comment__wrapper',
          '.comment-drawer__wrapper',
          '.board-comment-drawer',
          '.v-navigation-drawer__content',
          '.v-navigation-drawer',
        ]

        scrollSelectors.forEach((selector) => {
          const element = document.querySelector(selector)
          if (element) {
            element.addEventListener('scroll', handleScroll)
            //console.log(`스크롤 이벤트 리스너 등록됨: ${selector}`)
          } else {
            //console.log(`요소를 찾을 수 없음: ${selector}`)
          }
        })
      })
    }
    if (props.initialComments) {
      comments.value = props.initialComments
    } else {
      fetchComments(id)
    }
  },
  { immediate: true },
)

watch(
  () => open.value,
  async (isOpen) => {
    if (!isOpen) {
      // 이 컴포넌트가 닫히면 함께 닫아야 하는 오버레이들 정리
      actionOpen.value = false
      isReportOpen.value = false
      try {
        dialogConfirm.value?.closeConfirmDialog?.()
      } catch (_) {}

      // 스크롤 이벤트 리스너 제거
      const scrollSelectors = [
        '.comment-drawer__list',
        '.board-comment__wrapper',
        '.comment-drawer__wrapper',
        '.board-comment-drawer',
        '.v-navigation-drawer__content',
        '.v-navigation-drawer',
      ]

      scrollSelectors.forEach((selector) => {
        const element = document.querySelector(selector)
        if (element) {
          element.removeEventListener('scroll', handleScroll)
          console.log(`스크롤 이벤트 리스너 제거됨: ${selector}`)
        }
      })

      // 버튼 숨김
      showGoToFirstButton.value = false

      await nextTick()
      // 드로어가 닫힐 때 body 스크롤 재활성화
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
      document.documentElement.classList.remove('v-overlay-scroll-blocked')
    }
  },
)

// 댓글 입력창
const commentText = ref('')
const taRef = ref(null)

function autoResize(e) {
  const el = e?.target || taRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function onKeydown(e) {
  // Enter로 전송, Shift+Enter는 줄바꿈 유지
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    submit()
  }
}

async function submit() {
  if (!hasText.value) return
  const payload = buildPayload()

  try {
    if (isEditing.value) {
      await editComment(props.articleId, { ...payload, commentId: editingId.value })
      // 댓글 수정 시에는 현재 페이지 유지
      await fetchComments(props.articleId, currentPage.value)
    } else if (isReplying.value) {
      await saveComment(props.articleId, { ...payload, upperCommentId: upperComment.value })
      // 댓글 등록 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last')
      // fetchComments에서 currentPage가 마지막 페이지로 업데이트됨
    } else if (isPrivateComment.value) {
      await saveComment(props.articleId, { ...payload, isPrivate: true })
      // 댓글 등록 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last')
      // fetchComments에서 currentPage가 마지막 페이지로 업데이트됨
    } else {
      await saveComment(props.articleId, payload)
      // 댓글 등록 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last')
      // fetchComments에서 currentPage가 마지막 페이지로 업데이트됨
    }
    commentText.value = ''
    editingId.value = null
    upperComment.value = null
    isEditing.value = false
    isReplying.value = false
    isPrivateComment.value = false
    await nextTick()
    autoResize()
  } catch (err) {
    DEV_MODE && console.error(isEditing.value ? 'editComment 실패' : 'saveComment 실패', err)
  }
}

onMounted(() => {
  nextTick(() => autoResize())
  fetchComments()
})

// 1,000자 제한
const MAX_LEN = 1000
const charCount = computed(() => commentText.value.length)

/** 옵션 메뉴 */
// TODO: action과 report를 하나의 메서드로 리팩토링
function openActions(comment) {
  targetComment.value = comment
  targetId.value = comment.commentId
  actionOpen.value = true
  kebabType.value = 'action'
}
function openReport(comment) {
  targetComment.value = comment
  targetId.value = comment.commentId
  actionOpen.value = true
  kebabType.value = 'report'
}
function closeMenu() {
  actionOpen.value = false
  kebabType.value = ''
}

/** 댓글 수정 */
function onEdit(id) {
  targetId.value = id
  editingId.value = id

  const list = comments.value || []
  const target = list.find((c) => String(c.commentId) === String(id))
  commentText.value = target?.name ?? ''

  // 수정할 댓글의 upperCommentId 정보 저장
  if (target?.upperCommentId) {
    params.upperCommentId = target.upperCommentId
  } else {
    params.upperCommentId = null
  }

  nextTick(() => {
    autoResize()
    taRef.value?.focus()
    const len = commentText.value.length
    try {
      taRef.value?.setSelectionRange(len, len)
    } catch (_) {}
  })
}
/** 댓글 삭제 */
async function onDelete(id) {
  showDialogConfirm('정말 삭제하시겠습니까?', '삭제 후 복원이 불가능합니다.', '확인', () =>
    confirmDelete(id),
  )
}

/** 삭제 확인 */
const confirmDelete = async (id) => {
  try {
    const res = await delComment(id)
    if (res) {
      // 댓글 삭제 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last')
      // fetchComments에서 currentPage가 마지막 페이지로 업데이트됨
    } else {
      alert('댓글 삭제 실패') // 임시 조치
    }
  } catch (err) {
    DEV_MODE && console.error('게시판 댓글 삭제 실패', err)
  }
}

const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}, warning) => {
  dialogTitle.value = title
  dialogTitle2.value = title2
  dialogText.value = text
  dialogNextUrl.value = onConfirm
  warningTxt.value = warning
  dialogConfirm.value.openConfirmDialog()
}

/** 대댓글 */
const onNestedReplyClick = async (comment) => {
  if (!currentUserId.value || !loginUserId.value) return
  replyToAuthorName.value = comment.userName ?? ''
  isReplying.value = true
  upperComment.value = comment.commentId
}

/** 신고하기 드로어 오픈 */
const onReport = (comment) => {
  isReportOpen.value = true
  targetComment.value = comment
}
const closeReportDrawer = () => {
  isReportOpen.value = false
}

useBackLayer({
  id: 'board-comment-drawer',
  isOpen: open,
  closeFromBack: () => {
    open.value = false
  },
});
useBackLayer({
  id: 'board-comment-drawer-actions',
  isOpen: actionOpen,
  closeFromBack: closeMenu,
});
useBackLayer({
  id: 'board-comment-drawer-report',
  isOpen: isReportOpen,
  closeFromBack: closeReportDrawer,
});

/** 비밀댓글 설정 */
const setSecretComment = () => {
  isPrivateComment.value = !isPrivateComment.value
}

/** 첫 댓글로 이동 버튼 표시 여부 */
const showGoToFirstButton = ref(false)

/** 첫 댓글로 이동 */
const goToFirstComment = () => {
  console.log('첫 댓글로 이동 버튼 클릭됨')

  // 여러 가능한 스크롤 요소들을 시도
  const selectors = ['.board-comment__wrapper']

  let scrollApplied = false
  for (const selector of selectors) {
    const element = document.querySelector(selector)
    console.log(`스크롤 요소 확인: ${selector}`, element)

    if (element) {
      console.log(
        `요소 정보 - scrollHeight: ${element.scrollHeight}, clientHeight: ${element.clientHeight}, scrollTop: ${element.scrollTop}`,
      )

      if (element.scrollHeight > element.clientHeight) {
        element.scrollTop = 0
        console.log(`스크롤 적용됨: ${selector}`)
        scrollApplied = true
        break
      }
    }
  }

  if (!scrollApplied) {
    console.log('스크롤 적용 실패 - 모든 선택자에서 스크롤 불가')
  }
}

/** 스크롤 위치에 따른 버튼 표시/숨김 */
const handleScroll = (event) => {
  console.log('스크롤 이벤트 발생!', event.target)

  // 여러 스크롤 가능한 요소들 확인
  const scrollSelectors = [
    '.comment-drawer__list',
    '.board-comment__wrapper',
    '.comment-drawer__wrapper',
    '.board-comment-drawer',
    '.v-navigation-drawer__content',
    '.v-navigation-drawer',
  ]

  let maxScrollTop = 0

  scrollSelectors.forEach((selector) => {
    const element = document.querySelector(selector)
    if (element && element.scrollTop > 0) {
      console.log(`${selector} 스크롤 위치: ${element.scrollTop}`)
      maxScrollTop = Math.max(maxScrollTop, element.scrollTop)
    }
  })

  console.log(`최대 스크롤 위치: ${maxScrollTop}`)

  // 스크롤이 50px 이상 내려갔을 때만 버튼 표시
  showGoToFirstButton.value = maxScrollTop > 50
  console.log(`버튼 표시 여부: ${showGoToFirstButton.value}`)

  // 테스트용: 3초 후 강제로 버튼 표시
  if (!showGoToFirstButton.value) {
    setTimeout(() => {
      console.log('테스트: 버튼 강제 표시')
      showGoToFirstButton.value = true
    }, 3000)
  }
}

defineExpose({ refresh: () => fetchComments(props.articleId) })
</script>

<template>
  <!-- <Teleport to="body"> -->
  <VNavigationDrawer
    v-model="open"
    width="100%"
    :height="'100dvh'"
    location="bottom"
    temporary
    :scrim="true"
    touchless
    class="board-comment-drawer"
  >
    <div class="comment-drawer__wrapper st-mobile mobile-comment-drawer">
      <!-- 헤더 -->
      <div class="comment-drawer__header">
        <div class="comment-drawer__h-box">
          <span>댓글</span>
          <div class="comment-drawer__close" @click="open = false"></div>
        </div>
      </div>

      <!-- 댓글 리스트 -->
      <div class="comment-drawer__list">
        <BoardCommentList
          ref="commentListRef"
          :comments="comments"
          :currentUserId="currentUserId || loginUserId"
          :articleId="articleId"
          :currentPage="currentPage"
          :articleHasComment="articleHasComment"
          @openActions="openActions"
          @openReport="openReport"
          @onNestedReplyClick="onNestedReplyClick"
          @page-changed="handlePageChange"
        />
      </div>
      <!-- 댓글 입력창 -->
      <div
        v-if="currentUserId && loginUserId && articleHasComment === true"
        class="borad-comment__bottom"
      >
        <div v-if="isReplying" class="board-comment__replying-notice">
          <span
            ><span class="author-name">@ {{ replyToAuthorName }}</span> 님에게 답글 남기는 중</span
          >
          <div class="comment-drawer__close" @click="isReplying = false"></div>
        </div>
        <div class="board-comment__add-comment">
          <div class="board-comment__write-area">
            <textarea
              :id="`comment-${articleId ?? 'new'}`"
              ref="taRef"
              v-model="commentText"
              class="comment-input-plain"
              placeholder="댓글을 입력하세요"
              rows="1"
              @input="autoResize"
              @keydown="onKeydown"
              autocapitalize="sentences"
              autocomplete="off"
              autocorrect="off"
              :maxlength="MAX_LEN"
            />
            <div class="board-comment__comment-counter">{{ charCount }} / 1,000</div>
          </div>
        </div>
        <div class="board-comment__bar">
          <button class="board-comment__secret-btn" @click="setSecretComment">
            <div class="secret-comment-icon" :class="{ active: isPrivateComment }" />
          </button>
          <button
            class="board-comment__add-btn"
            :class="{ active: hasText }"
            :disabled="!hasText"
            @click="submit"
          >
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showGoToFirstButton"
      class="goToFirstComment top-btn-move-box"
      :class="{ 'st-login-user-top-box': !loginUserId }"
    >
      <button class="top-btn-move-btn" @click="goToFirstComment">첫 댓글로 이동</button>
    </div>
  </VNavigationDrawer>

  <!-- </Teleport> -->
  <CommentActionMenu
    v-model="actionOpen"
    :target-id="targetId"
    :type="kebabType"
    @edit="onEdit"
    @delete="onDelete"
    @report="onReport"
    @cancel="closeMenu"
  />

  <CommentReportDrawer
    v-model="isReportOpen"
    :targetComment="targetComment"
    @close="closeReportDrawer"
  />

  <ConfirmComponent
    ref="dialogConfirm"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="warningTxt"
  />
</template>
<style lang="scss">
.comment-drawer__wrapper.st-mobile {
  padding-bottom: 50px;
}
.comment-drawer__wrapper.st-mobile.mobile-comment-drawer {
  padding-bottom: 0;
}
.top-btn-move-box {
  position: fixed;
  bottom: 186px;
  right: 8px;
  z-index: 10;
  &.st-login-user-top-box {
    bottom: 56px;
  }
  .top-btn-move-btn {
    width: 136px;
    height: 40px;
    background-color: #fff;
    border: 1px solid #ebebeb;
    color: var(--gray-100-white, #fff);
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 125% */
    display: flex;
    padding: 10px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    background: url('/src/assets/images_manse/rivet-icons_page-top.svg') #eb4c10 10px center
      no-repeat;
    background-size: 20px 20px;
    padding-left: 40px;
    white-space: nowrap;
  }
}
</style>
